type BundleConfigurableVariant = {
  variant_id?: number | string
}

type BundleProduct = {
  bundle_group_product_id: number | string
  is_configurable?: boolean
  is_configured?: boolean
  nested_data?: BundleConfigurableVariant[]
  product_sku_id?: unknown
  variants?: BundleConfigurableVariant[]
}

type ConfigurableSelection = Record<
  number | string,
  {
    selected_options?: Record<number | string, unknown>
  }
>

type ApiError = {
  response?: {
    data?: {
      code?: unknown
      message?: unknown
    }
  }
}

type CartRemovalOutcome = {
  type?: unknown
  message?: unknown
  product_name?: unknown
  details?: unknown
}

type BundleSelectionChangedNotification = {
  message: string
  productName: string | null
  productPath: string | null
}

const BUNDLE_SELECTION_CHANGED_CODE = 'bundle_selection_changed'
const DEFAULT_ADD_TO_CART_ERROR = 'Product could not be added to the cart.'

export type BreakableBundleConfigSelectionStatus = 'ready' | 'incomplete' | 'resolving' | 'unavailable'

const hasConcreteProductSkuId = (productSkuId: unknown) => {
  if (typeof productSkuId === 'number') {
    return Number.isInteger(productSkuId) && productSkuId > 0
  }

  return typeof productSkuId === 'string' && /^[1-9]\d*$/.test(productSkuId)
}

export const getBreakableBundleConfigSelectionStatus = (
  bundleProducts: BundleProduct[],
  configurableSelection: ConfigurableSelection
) => {
  const statuses = bundleProducts
    .filter(product => product.is_configurable && !product.is_configured)
    .map<BreakableBundleConfigSelectionStatus>(product => {
      const variants = (product.variants ?? product.nested_data ?? []).filter(
        variant => variant.variant_id !== undefined && variant.variant_id !== null
      )
      const selectedOptions = configurableSelection[product.bundle_group_product_id]?.selected_options
      const selectionsComplete =
        variants.length > 0 &&
        variants.every(variant => {
          const selectedOption = selectedOptions?.[variant.variant_id]
          return selectedOption !== undefined && selectedOption !== null
        })

      if (!selectionsComplete) return 'incomplete'
      if (product.product_sku_id === 'not-found') return 'unavailable'
      return hasConcreteProductSkuId(product.product_sku_id) ? 'ready' : 'resolving'
    })

  if (statuses.includes('incomplete')) return 'incomplete'
  if (statuses.includes('unavailable')) return 'unavailable'
  if (statuses.includes('resolving')) return 'resolving'
  return 'ready'
}

export const areBreakableBundleConfigSelectionsResolved = (
  bundleProducts: BundleProduct[],
  configurableSelection: ConfigurableSelection
) => getBreakableBundleConfigSelectionStatus(bundleProducts, configurableSelection) === 'ready'

export const getBundleSelectionChangedMessage = (error: unknown) => {
  if ((error as ApiError)?.response?.data?.code !== BUNDLE_SELECTION_CHANGED_CODE) return null

  const message = (error as ApiError).response?.data?.message
  return typeof message === 'string' && message.trim() ? message : DEFAULT_ADD_TO_CART_ERROR
}

export const getRemovedBundleSelectionChangedNotification = (
  removedItems: CartRemovalOutcome[] | null | undefined
): BundleSelectionChangedNotification | null => {
  const outcome = removedItems?.find(item => item.type === BUNDLE_SELECTION_CHANGED_CODE)
  if (typeof outcome?.message !== 'string' || !outcome.message.trim()) return null

  const productName =
    typeof outcome.product_name === 'string' && outcome.product_name.trim() ? outcome.product_name : null
  const productPath =
    outcome.details && typeof outcome.details === 'object' && 'product_path' in outcome.details
      ? outcome.details.product_path
      : null

  return {
    message: outcome.message,
    productName,
    productPath: typeof productPath === 'string' && productPath.startsWith('/product/') ? productPath : null
  }
}
